import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2, X } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { useEvents, type NewEvent } from "@/lib/hooks";
import { EVENT_CATEGORIES, type EventCategory } from "@/lib/meta";
import { addDays, fmtLongDay, isoDate, parseDate, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";
import { WhoDot } from "@/components/member-avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function CalendarPage() {
  const todayStr = todayISO();
  const [view, setView] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [selected, setSelected] = useState(todayStr);
  const [adding, setAdding] = useState(false);

  const monthStart = new Date(view.year, view.month, 1);
  const gridStart = addDays(monthStart, -monthStart.getDay());
  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)), [gridStart.getTime()]);

  const { events, addEvent, deleteEvent } = useEvents(isoDate(days[0]!), isoDate(days[41]!));

  const byDate = useMemo(() => {
    const map = new Map<string, typeof events>();
    for (const e of events) {
      const arr = map.get(e.date) ?? [];
      arr.push(e);
      map.set(e.date, arr);
    }
    return map;
  }, [events]);

  const selectedEvents = (byDate.get(selected) ?? []).slice().sort((a, b) => (a.time ?? "").localeCompare(b.time ?? ""));

  const move = (delta: number) => {
    const m = view.month + delta;
    setView({ year: view.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-3 md:p-4">
          {/* Month header */}
          <div className="mb-3 flex items-center justify-between">
            <div className="text-lg font-semibold">
              {MONTHS[view.month]} {view.year}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const d = new Date();
                  setView({ year: d.getFullYear(), month: d.getMonth() });
                  setSelected(todayStr);
                }}
              >
                Today
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={() => move(-1)} aria-label="Previous month">
                <ChevronLeft />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={() => move(1)} aria-label="Next month">
                <ChevronRight />
              </Button>
            </div>
          </div>

          {/* Weekday labels */}
          <div className="grid grid-cols-7 text-center text-xs font-medium text-muted-foreground">
            {WEEKDAYS.map((w) => (
              <div key={w} className="pb-1">
                {w}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const ds = isoDate(d);
              const inMonth = d.getMonth() === view.month;
              const isToday = ds === todayStr;
              const isSelected = ds === selected;
              const dayEvents = byDate.get(ds) ?? [];
              return (
                <button
                  key={ds}
                  onClick={() => setSelected(ds)}
                  className={cn(
                    "flex aspect-square flex-col items-center rounded-lg p-1 text-sm transition-colors md:aspect-[4/3]",
                    !inMonth && "text-muted-foreground/40",
                    isSelected ? "bg-primary text-primary-foreground" : "hover:bg-accent",
                  )}
                >
                  <span
                    className={cn(
                      "flex size-6 items-center justify-center rounded-full text-xs",
                      isToday && !isSelected && "bg-primary/15 font-bold text-primary",
                      isToday && isSelected && "font-bold",
                    )}
                  >
                    {d.getDate()}
                  </span>
                  <div className="mt-0.5 flex flex-wrap justify-center gap-0.5">
                    {dayEvents.slice(0, 4).map((e, i) => (
                      <span
                        key={`${e.id}-${i}`}
                        className={cn(
                          "size-1.5 rounded-full",
                          isSelected ? "bg-primary-foreground/80" : EVENT_CATEGORIES[e.category].dot,
                        )}
                      />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Agenda for selected day */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{fmtLongDay(parseDate(selected))}</h3>
        <Button size="sm" onClick={() => setAdding((v) => !v)}>
          {adding ? <X /> : <Plus />}
          {adding ? "Cancel" : "Add event"}
        </Button>
      </div>

      {adding && (
        <AddEventForm
          defaultDate={selected}
          onCancel={() => setAdding(false)}
          onSubmit={async (e) => {
            await addEvent(e);
            setSelected(e.date);
            setAdding(false);
          }}
        />
      )}

      <div className="space-y-2">
        {selectedEvents.length === 0 && !adding && (
          <p className="py-4 text-center text-sm text-muted-foreground">No events this day.</p>
        )}
        {selectedEvents.map((e) => (
          <EventRow key={`${e.id}-${e.date}`} event={e} onDelete={() => deleteEvent(e.id)} />
        ))}
      </div>
    </div>
  );
}

function EventRow({ event, onDelete }: { event: { id: string; title: string; time: string | null; allDay: boolean; who: string; category: EventCategory }; onDelete: () => void }) {
  const { memberById } = useAuth();
  const member = event.who === "both" ? null : memberById(event.who);
  return (
    <div className="group flex items-center gap-3 rounded-lg border bg-card p-3">
      <div className="w-16 shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
        {event.allDay || !event.time ? "All day" : event.time}
      </div>
      <span className={cn("size-2.5 rounded-full", EVENT_CATEGORIES[event.category].dot)} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{event.title}</div>
        <div className="text-xs text-muted-foreground">{EVENT_CATEGORIES[event.category].label}</div>
      </div>
      <WhoDot both={event.who === "both"} color={member?.color} />
      <Button variant="ghost" size="icon-sm" onClick={onDelete} className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" aria-label="Delete event">
        <Trash2 />
      </Button>
    </div>
  );
}

function AddEventForm({ defaultDate, onSubmit, onCancel }: { defaultDate: string; onSubmit: (e: NewEvent) => Promise<void>; onCancel: () => void }) {
  const { members } = useAuth();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [who, setWho] = useState("both");
  const [category, setCategory] = useState<EventCategory>("family");
  const [recurrence, setRecurrence] = useState<NewEvent["recurrence"]>("none");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSubmit({
        title: title.trim(),
        date,
        time: allDay || !time ? null : time,
        allDay,
        who,
        category,
        recurrence,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <Input placeholder="Event title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Time">
            <Input type="time" value={time} disabled={allDay} onChange={(e) => setTime(e.target.value)} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="size-4" />
          All day
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="For">
            <NativeSelect value={who} onChange={setWho}>
              <option value="both">Both of us</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Category">
            <NativeSelect value={category} onChange={(v) => setCategory(v as EventCategory)}>
              {Object.entries(EVENT_CATEGORIES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </div>
        <Field label="Repeat">
          <NativeSelect value={recurrence} onChange={(v) => setRecurrence(v as NewEvent["recurrence"])}>
            <option value="none">Does not repeat</option>
            <option value="daily">Every day</option>
            <option value="weekly">Every week</option>
            <option value="monthly">Every month</option>
            <option value="yearly">Every year</option>
          </NativeSelect>
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !title.trim()}>
            Add event
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function NativeSelect({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      {children}
    </select>
  );
}
